import { useEffect, useState, type FormEvent } from "react";
import { apiErrorMessage, type Me, type RaceApi } from "../api";
import { AppHeader } from "./AppHeader";

interface SignInScreenProps {
  api: RaceApi;
  me: Me | null;
  testLoginAvailable: boolean;
  reason: string | null;
  returnToRace: string | null;
  onSignedIn(me: Me): void;
  onBack(): void;
  onHome(): void;
  onProfile(): void;
  onHistory(): void;
  onSignOut(): void;
}

type Step = "form" | "sent";

export function SignInScreen({
  api,
  me,
  testLoginAvailable,
  reason,
  returnToRace,
  onSignedIn,
  onBack,
  onHome,
  onProfile,
  onHistory,
  onSignOut,
}: SignInScreenProps) {
  const [step, setStep] = useState<Step>("form");
  const [email, setEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("email") ?? "";
  });
  const [testEmail, setTestEmail] = useState(() => {
    if (typeof window === "undefined") return "e2e-rider@cimara.test";
    return (
      new URLSearchParams(window.location.search).get("email") ??
      "e2e-rider@cimara.test"
    );
  });
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    if (step !== "sent") return;
    const timer = window.setInterval(() => {
      void api
        .getMe()
        .then((next) => {
          if (next) onSignedIn(next);
        })
        .catch(() => {
          // Keep waiting; the user can resend the link.
        });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [api, onSignedIn, step]);

  const callbackURL =
    typeof window !== "undefined"
      ? `${window.location.origin}/${returnToRace ? `?next=${encodeURIComponent(returnToRace)}` : ""}`
      : undefined;

  const sendMagicLink = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const value = email.trim();
    if (!value) {
      setError("Enter the email address for your account.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await api.sendMagicLink(value, callbackURL);
      setSentTo(value);
      setStep("sent");
    } catch (cause) {
      setError(apiErrorMessage(cause));
    } finally {
      setSending(false);
    }
  };

  const testLogin = async (): Promise<void> => {
    const value = (testEmail || email).trim();
    if (!value) {
      setError("Enter a test email address.");
      return;
    }
    setTesting(true);
    setError(null);
    try {
      await api.testLogin(value);
      const next = await api.getMe();
      if (!next) {
        setError("Test login succeeded but no session was created.");
        return;
      }
      onSignedIn(next);
    } catch (cause) {
      setError(apiErrorMessage(cause));
    } finally {
      setTesting(false);
    }
  };

  return (
    <main className="page-screen auth-screen">
      <AppHeader
        me={me}
        onHome={onHome}
        onSignIn={onBack}
        onSignOut={onSignOut}
        onProfile={onProfile}
        onHistory={onHistory}
        compact
      />

      <div className="auth-card">
        <p className="eyebrow">
          <span className="eyebrow__line" />
          {step === "sent" ? "Check your email" : "Sign in"}
        </p>

        {reason ? <p className="auth-reason">{reason}</p> : null}

        {step === "sent" ? (
          <div className="auth-success" role="status">
            <h1 className="auth-title">Check your inbox.</h1>
            <p>
              We sent a magic link to <strong>{sentTo}</strong>. Open it on this device
              to finish signing in. The link expires in five minutes.
            </p>
            <div className="auth-actions">
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  setStep("form");
                  setSentTo(null);
                }}
              >
                Use a different email
              </button>
              <button type="button" className="text-button" onClick={onBack}>
                Back to calendar
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="auth-title">Race with an account.</h1>
            <p className="auth-sub">
              Magic links only. We never ask for a password, and a guest can still
              free ride without one.
            </p>

            <form className="auth-form" onSubmit={(event) => void sendMagicLink(event)}>
              <label className="field">
                <span className="field__label">Email</span>
                <input
                  className="field__input"
                  data-testid="magic-link-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
                <span className="field__hint">
                  We send one link. No password, no social login.
                </span>
              </label>

              <button
                type="submit"
                className="ride-button ride-button--compact auth-submit"
                data-testid="magic-link-submit"
                disabled={sending}
              >
                {sending ? "Sending…" : "Send magic link"}
              </button>
            </form>

            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}

            {testLoginAvailable ? (
              <div className="test-login">
                <div className="test-login__head">
                  <span className="panel-kicker">Local / E2E only</span>
                  <span className="test-login__badge">Test login</span>
                </div>
                <p className="test-login__note">
                  This server exposes the test-auth endpoint. It is never shown when
                  the endpoint is absent.
                </p>
                <div className="test-login__row">
                  <input
                    className="field__input"
                    type="email"
                    aria-label="Test login email"
                    value={testEmail}
                    onChange={(event) => setTestEmail(event.target.value)}
                  />
                  <button
                    type="button"
                    className="ride-button ride-button--compact"
                    data-testid="test-login"
                    onClick={() => void testLogin()}
                    disabled={testing}
                  >
                    {testing ? "Signing in…" : "Test login"}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="auth-actions">
              <button type="button" className="text-button" onClick={onBack}>
                Back to calendar
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
